import { useState, useRef, useEffect } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { getCabinaData } from '@/mocks/cabinaContent';
import { topFans } from '@/mocks/topFans';
import { useCabinaMessages } from '@/context/CabinaContext';

interface CabinaSidePanelProps {
  station: RadioStation;
  isOpen: boolean;
  onClose: () => void;
}

type SectionTab = 'chat' | 'saludos' | 'canciones' | 'encuestas' | 'concursos' | 'topfans' | 'vip';

const sectionTabs: { id: SectionTab; icon: string; label: string }[] = [
  { id: 'chat', icon: 'ri-chat-3-line', label: 'Chat' },
  { id: 'saludos', icon: 'ri-chat-heart-line', label: 'Saludos' },
  { id: 'canciones', icon: 'ri-music-line', label: 'Canciones' },
  { id: 'encuestas', icon: 'ri-bar-chart-line', label: 'Encuestas' },
  { id: 'concursos', icon: 'ri-trophy-line', label: 'Concursos' },
  { id: 'topfans', icon: 'ri-star-line', label: 'Top Fans' },
  { id: 'vip', icon: 'ri-vip-crown-line', label: 'VIP' },
];

const moderadoresFake = [
  { name: 'María Solís', avatar: 'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20female%20moderator%20portrait%2C%20warm%20friendly%20expression%2C%20neat%20casual%20look%2C%20soft%20studio%20lighting%2C%20clean%20background%2C%20approachable%20professional%20headshot&width=80&height=80&seq=mod-maria-s&orientation=squarish', role: 'Moderadora' },
  { name: 'Diego Ramírez', avatar: 'https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20moderator%20portrait%2C%20friendly%20professional%20expression%2C%20casual%20modern%20style%2C%20clean%20studio%20background%2C%20approachable%20headshot%20photography&width=80&height=80&seq=mod-diego-r&orientation=squarish', role: 'Moderador' },
];

const quickPollActive = {
  question: '¿Qué género quieren escuchar ahora?',
  options: [
    { label: 'Pop', votes: 142, pct: 38 },
    { label: 'Rock', votes: 98, pct: 26 },
    { label: 'Reggaetón', votes: 76, pct: 20 },
    { label: 'Regional Mexicano', votes: 62, pct: 16 },
  ],
  totalVotes: 378,
  endsIn: '3:42',
};

const concursosActivos = [
  { id: 1, title: 'Trivia Matutina', prize: 'Tarjeta $50', icon: 'ri-question-answer-line', color: 'bg-emerald-500' },
  { id: 2, title: 'Código Secreto', prize: '$100', icon: 'ri-key-2-line', color: 'bg-amber-500' },
  { id: 3, title: 'Sé Parte del Show', prize: '15 min al aire', icon: 'ri-mic-line', color: 'bg-rose-500' },
];

const vipHighlight = [
  { name: 'Camila Restrepo', tier: 'Oro', avatar: 'https://readdy.ai/api/search-image?query=Young%20latina%20woman%20in%20her%20twenties%2C%20warm%20friendly%20smile%2C%20casual%20urban%20style%2C%20clean%20soft%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=vip-camila-cabina&orientation=squarish' },
  { name: 'Javier Mendoza', tier: 'Oro', avatar: 'https://readdy.ai/api/search-image?query=Young%20latino%20man%20in%20his%20twenties%2C%20sporty%20look%2C%20energetic%20expression%2C%20clean%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=vip-javier-cabina&orientation=squarish' },
  { name: 'Isabella Torres', tier: 'Oro', avatar: 'https://readdy.ai/api/search-image?query=Young%20latina%20woman%20in%20her%20twenties%2C%20stylish%20creative%20look%2C%20warm%20genuine%20expression%2C%20clean%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=vip-isabella-cabina&orientation=squarish' },
  { name: 'Felipe Gutiérrez', tier: 'Oro', avatar: 'https://readdy.ai/api/search-image?query=Young%20latino%20man%20in%20his%20twenties%2C%20charismatic%20expression%2C%20casual%20streetwear%20style%2C%20clean%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=vip-felipe-cabina&orientation=squarish' },
];

const embajadoresFake = [
  { name: 'Laura Santamaría', nivel: 'Embajadora', avatar: 'https://readdy.ai/api/search-image?query=Young%20latina%20woman%20in%20her%20twenties%2C%20genuine%20warm%20smile%2C%20casual%20elegant%20style%2C%20clean%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=emb-laura-cabina&orientation=squarish' },
  { name: 'Daniel Ortega', nivel: 'Embajador', avatar: 'https://readdy.ai/api/search-image?query=Young%20latino%20man%20in%20his%20twenties%2C%20relaxed%20smile%2C%20casual%20t-shirt%2C%20clean%20portrait%2C%20natural%20lighting%2C%20editorial%20photography&width=80&height=80&seq=emb-daniel-cabina&orientation=squarish' },
];

export default function CabinaSidePanel({ station, isOpen, onClose }: CabinaSidePanelProps) {
  const cabina = getCabinaData(station.id);
  const [activeSection, setActiveSection] = useState<SectionTab>('chat');
  const [chatInput, setChatInput] = useState('');
  const { messages, addMessage, seedMessages } = useCabinaMessages();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onlineCount = cabina?.onlineListeners || 0;
  const stationFans = topFans.filter(f => f.radioId === station.id).slice(0, 5);
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (cabina && !hasSeededRef.current) {
      seedMessages([...cabina.messages]);
      hasSeededRef.current = true;
    }
  }, [cabina, seedMessages]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, activeSection]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const getTypeForSection = (section: SectionTab) => {
    if (section === 'saludos') return 'shoutout' as const;
    if (section === 'canciones') return 'song_request' as const;
    return 'message' as const;
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    addMessage({
      from: 'Tú',
      text: chatInput.trim(),
      type: getTypeForSection(activeSection),
    });
    setChatInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChat();
    }
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:bg-black/20"
          onClick={onClose}
        ></div>
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 bg-background-50 border-l border-background-200/70 flex flex-col transition-transform duration-300 ease-in-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } w-full md:w-[460px]`}
      >
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 bg-background-50 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex items-center">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              <span className="absolute w-3 h-3 bg-red-500 rounded-full animate-ping opacity-40"></span>
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-foreground-950 truncate">CABINA EN VIVO</h2>
              <p className="text-xs text-foreground-500">{formatNumber(onlineCount)} personas conectadas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors shrink-0"
          >
            <i className="ri-close-line text-foreground-600 text-lg"></i>
          </button>
        </div>

        {/* ── HOST + MODS BAR ── */}
        {cabina && (
          <div className="px-5 py-3 border-b border-background-200/70 bg-background-50/80 shrink-0">
            <div className="flex items-center gap-3">
              {/* Host */}
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <div className="relative shrink-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200/70 border-2 border-red-500/30">
                    <img src={cabina.hostPhotoUrl} alt={cabina.hostName} className="w-full h-full object-cover" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 truncate">{cabina.hostName}</p>
                  <p className="text-[11px] text-accent-600 font-medium">Locutor · Al aire</p>
                </div>
              </div>

              {/* Mods */}
              <div className="hidden sm:flex items-center gap-1">
                {moderadoresFake.map((mod) => (
                  <div key={mod.name} className="relative group shrink-0" title={`${mod.name} - ${mod.role}`}>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200/70 border-2 border-secondary-300">
                      <img src={mod.avatar} alt={mod.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                ))}
                <span className="text-[10px] text-foreground-400 font-medium ml-1 whitespace-nowrap">+ Mods</span>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION TABS ── */}
        <div className="px-4 py-2 border-b border-background-200/70 bg-background-50 shrink-0 overflow-x-auto">
          <div className="flex items-center gap-1">
            {sectionTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap shrink-0 ${
                  activeSection === tab.id
                    ? 'bg-foreground-950 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icon} text-[13px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── CONTENT ── */}
        <div className="flex-1 overflow-y-auto">
          {/* ===== CHAT ===== */}
          {activeSection === 'chat' && (
            <div className="px-4 py-3 space-y-2">
              {cabina?.status === 'online' && (
                <div className="flex items-center justify-center py-2">
                  <div className="flex items-center gap-2 bg-background-100 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
                    <span className="text-[11px] text-foreground-500 font-medium">
                      {cabina.hostName} está al aire · {formatNumber(onlineCount)} escuchando
                    </span>
                  </div>
                </div>
              )}
              {messages.map((msg) => {
                const isSelf = msg.from === 'Tú';
                const isHost = msg.from === cabina?.hostName;
                return (
                  <div key={msg.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex items-end gap-2 max-w-[85%] ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-background-200/70 flex-shrink-0">
                        {isHost ? (
                          <img src={cabina.hostPhotoUrl} alt={msg.from} className="w-full h-full object-cover" />
                        ) : isSelf ? (
                          <div className="w-full h-full bg-primary-500 flex items-center justify-center">
                            <i className="ri-user-line text-[10px] text-background-50"></i>
                          </div>
                        ) : msg.avatarUrl ? (
                          <img src={msg.avatarUrl} alt={msg.from} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-background-300 flex items-center justify-center">
                            <i className="ri-user-line text-[10px] text-foreground-500"></i>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className={`px-3 py-2 text-sm ${
                          isSelf
                            ? 'bg-primary-500 text-background-50 rounded-2xl rounded-br-md'
                            : isHost
                              ? 'bg-accent-100 text-accent-900 rounded-2xl rounded-bl-md'
                              : 'bg-background-100 text-foreground-800 rounded-2xl rounded-bl-md'
                        }`}>
                          <p className="text-[10px] font-semibold opacity-70 mb-0.5">{msg.from}</p>
                          <p className="leading-relaxed break-words">{msg.text}</p>
                        </div>
                        <p className={`text-[10px] text-foreground-400 mt-0.5 ${isSelf ? 'text-right mr-1' : 'ml-1'}`}>{msg.time}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef}></div>
            </div>
          )}

          {/* ===== SALUDOS ===== */}
          {activeSection === 'saludos' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-500 mb-4">Saludos de oyentes que están siendo leídos al aire</p>
              {messages.filter(m => m.type === 'shoutout').length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-chat-heart-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">No hay saludos aún</p>
                  <p className="text-xs text-foreground-400 mt-1">¡Sé el primero en enviar uno!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.filter(m => m.type === 'shoutout').map((msg) => (
                    <div key={msg.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        {msg.avatarUrl && <img src={msg.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />}
                        <span className="text-sm font-semibold text-foreground-950">{msg.from}</span>
                        <span className="text-[10px] text-foreground-400">{msg.time}</span>
                      </div>
                      <p className="text-sm text-foreground-700">{msg.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== CANCIONES ===== */}
          {activeSection === 'canciones' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-500 mb-4">Canciones solicitadas por la audiencia</p>
              {messages.filter(m => m.type === 'song_request').length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-music-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">No hay canciones solicitadas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.filter(m => m.type === 'song_request').map((msg) => (
                    <div key={msg.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                        <i className="ri-music-2-line text-accent-600 text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground-950 truncate">{msg.text}</p>
                        <p className="text-[11px] text-foreground-500">{msg.from} · {msg.time}</p>
                      </div>
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Pendiente</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== ENCUESTAS ===== */}
          {activeSection === 'encuestas' && (
            <div className="px-4 py-4">
              <div className="bg-background-50 border border-background-200/70 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-bar-chart-line text-accent-600"></i>
                  <span className="text-[10px] font-bold text-accent-700 uppercase tracking-wider bg-accent-100 px-2 py-0.5 rounded-full">ENCUESTA ACTIVA</span>
                </div>
                <h4 className="text-sm font-bold text-foreground-950 mb-4">{quickPollActive.question}</h4>
                <div className="space-y-2.5">
                  {quickPollActive.options.map((opt) => (
                    <div key={opt.label} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-foreground-700">{opt.label}</span>
                        <span className="text-xs font-semibold text-foreground-500">{opt.pct}%</span>
                      </div>
                      <div className="w-full h-2.5 bg-background-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent-500 rounded-full transition-all duration-500"
                          style={{ width: `${opt.pct}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                  <span className="text-xs text-foreground-500">{quickPollActive.totalVotes} votos</span>
                  <span className="flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-time-line"></i>
                    Termina en {quickPollActive.endsIn}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ===== CONCURSOS ===== */}
          {activeSection === 'concursos' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-500 mb-4">Concursos activos en esta radio</p>
              <div className="space-y-3">
                {concursosActivos.map((c) => (
                  <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:border-accent-300/60 transition-colors">
                    <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center shrink-0`}>
                      <i className={`${c.icon} text-background-50 text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-semibold text-foreground-950">{c.title}</h5>
                      <p className="text-xs text-foreground-500">Premio: {c.prize}</p>
                    </div>
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">ACTIVO</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== TOP FANS ===== */}
          {activeSection === 'topfans' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-500 mb-4">Los oyentes más fieles de {station.name}</p>
              {stationFans.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-star-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">Aún no hay Top Fans</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stationFans.map((fan, i) => (
                    <div key={fan.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center shrink-0 text-sm font-bold text-foreground-500">{i + 1}</div>
                      <img src={fan.photoUrl} alt={fan.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground-950 truncate">{fan.name}</p>
                        <p className="text-[11px] text-foreground-500">{fan.hoursListened}h escuchadas · {fan.comments} mensajes</p>
                      </div>
                      {fan.badge === 'oro' && <span className="text-lg">🥇</span>}
                      {fan.badge === 'plata' && <span className="text-lg">🥈</span>}
                      {fan.badge === 'bronce' && <span className="text-lg">🥉</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== VIP ===== */}
          {activeSection === 'vip' && (
            <div className="px-4 py-4">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-vip-crown-line text-amber-500 text-lg"></i>
                  <h4 className="text-sm font-bold text-foreground-950">Miembros VIP</h4>
                </div>
                <p className="text-xs text-foreground-500">Oyentes con membresía premium del club VIP</p>
              </div>
              <div className="space-y-2.5">
                {vipHighlight.map((vip) => (
                  <div key={vip.name} className="bg-background-50 border border-background-200/70 rounded-xl p-3 flex items-center gap-3">
                    <div className="relative shrink-0">
                      <img src={vip.avatar} alt={vip.name} className="w-10 h-10 rounded-full object-cover" />
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                        <i className="ri-vip-crown-fill text-background-50 text-[10px]"></i>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground-950">{vip.name}</p>
                      <p className="text-[11px] text-amber-700 font-medium">Tier {vip.tier}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-background-200/70">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-award-line text-accent-500 text-lg"></i>
                  <h4 className="text-sm font-bold text-foreground-950">Embajadores</h4>
                </div>
                <p className="text-xs text-foreground-500 mb-3">Representantes oficiales de la comunidad</p>
                <div className="space-y-2.5">
                  {embajadoresFake.map((emb) => (
                    <div key={emb.name} className="bg-background-50 border border-background-200/70 rounded-xl p-3 flex items-center gap-3">
                      <div className="relative shrink-0">
                        <img src={emb.avatar} alt={emb.name} className="w-10 h-10 rounded-full object-cover" />
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center">
                          <i className="ri-shield-star-fill text-background-50 text-[10px]"></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground-950">{emb.name}</p>
                        <p className="text-[11px] text-accent-700 font-medium">{emb.nivel}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── INPUT BAR ── */}
        <div className="px-4 py-3 border-t border-background-200/70 bg-background-50 shrink-0">
          {(activeSection === 'chat' || activeSection === 'saludos' || activeSection === 'canciones') ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-background-100 rounded-full px-4 py-2.5 border border-background-200/70">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    activeSection === 'chat' ? 'Escribe un mensaje...' :
                    activeSection === 'saludos' ? 'Envía un saludo al aire...' :
                    'Solicita una canción...'
                  }
                  className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                onClick={sendChat}
                disabled={!chatInput.trim()}
                className="w-10 h-10 rounded-full bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-200 disabled:cursor-not-allowed text-background-50 flex items-center justify-center cursor-pointer transition-all shrink-0"
              >
                <i className="ri-send-plane-fill text-base"></i>
              </button>
            </div>
          ) : (
            <p className="text-center text-xs text-foreground-400 py-1">Selecciona Chat, Saludos o Canciones para enviar mensajes</p>
          )}
        </div>
      </div>
    </>
  );
}